import { useEffect, useMemo, useRef, useState, type FormEvent, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Icon } from "@majourneys/icons";
import { useThreadAnchor } from "../../ai-concierge/hooks/use-thread-anchor";
import { useAiChat, type ChatLang } from "../../ai-concierge/hooks/use-ai-chat";
import { useChatHistory } from "../../ai-concierge/hooks/use-chat-history";
import { ChatHistoryMenu } from "../../ai-concierge/components/chat-history-menu";
import { ConciergeSignInGate } from "../../ai-concierge/components/concierge-sign-in-gate";
import {
  AssistantTurn,
  ChatError,
  PendingTurn,
  UserBubble,
} from "../../ai-concierge/components/assistant-turn";
import { ChatComposer } from "../../ai-concierge/components/chat-composer";
import { ChatWelcome } from "../../ai-concierge/components/chat-welcome";
import { MatchPreviewProvider } from "../../ai-concierge/components/match-preview";
import { ChatToolbar, ChatToolbarButton } from "../../ai-concierge/components/chat-toolbar";
import { ChatShareButton } from "../../ai-concierge/components/chat-share-button";
import { resumeChat } from "../../ai-concierge/api/chat-share.api";
import { SOFT } from "../../ai-concierge/components/soft-tokens";
import {
  useChatMapSize,
  useDiscoverActions,
  useTripActions,
  useTripContext,
} from "@majourneys/discover";
import {
  buildTripFromItinerary,
  mergeItineraryIntoTrip,
  countStops,
  type AiMatch,
  type DiscoverPhoneChat,
  type Itinerary,
} from "@majourneys/discover";

/**
 * In-section AI chat for the Discover tab. Pairs with the existing
 * DiscoverStage map on the right — every assistant turn pushes its
 * match slugs into the shared discover state so the map can re-pin
 * itself onto the latest answer (see `useFilteredItems`).
 *
 * Same parts as the modal's GlassConcierge (turns, composer, welcome), laid
 * out as a left panel instead of a floating window. Uses the same `useAiChat`
 * hook so conversation history, error handling, abort, and i18n are shared.
 */
export function DiscoverChat({ phone }: { phone?: DiscoverPhoneChat } = {}) {
  const { i18n, t } = useTranslation();
  const lang = (i18n.language?.slice(0, 2) ?? "en") as ChatLang;
  const history = useChatHistory();
  const {
    messages,
    loading,
    error,
    send,
    stop,
    retry,
    retryFailed,
    isLive,
    setMessages,
    gated,
    gatedQuery,
    clearGate,
  } = useAiChat({
    lang,
    limit: 20,
  });
  const { setAiMatchSlugs, setUtilityMode } = useDiscoverActions();
  const { createTripRaw, replaceTrip, setActiveTrip } = useTripActions();
  const { state: tripState } = useTripContext();
  const activeTrip = tripState.trips.find((tr) => tr.id === tripState.activeTripId) ?? null;

  const addItineraryToTrip = (itinerary: Itinerary, itinMatches: AiMatch[], mode: "new" | "active") => {
    const fallbackName = t("trip.ai_default_name");
    if (mode === "active" && activeTrip) {
      const merged = mergeItineraryIntoTrip(activeTrip, itinerary, itinMatches);
      if (countStops(merged) === countStops(activeTrip)) return;
      replaceTrip(merged);
      setActiveTrip(merged.id);
    } else {
      const built = buildTripFromItinerary(itinerary, itinMatches, fallbackName);
      if (countStops(built) === 0) return;
      createTripRaw(built);
      setActiveTrip(built.id);
    }
    setUtilityMode("trips");
  };

  const [input, setInput] = useState("");
  const [lastQuery, setLastQuery] = useState("");
  /** The earlier message being edited (C2); the next send replaces it. */
  const [editingId, setEditingId] = useState<string | null>(null);

  // Hydrate from saved history exactly once after the hook has read
  // localStorage. The `loaded` flag prevents the persist→re-hydrate
  // loop where our own writes would otherwise pull themselves back in.
  const hydratedRef = useRef(false);
  const { persistMessages, activeMessages, loaded } = history;
  useEffect(() => {
    if (hydratedRef.current) return;
    if (!loaded) return;
    if (activeMessages.length > 0) setMessages(activeMessages);
    hydratedRef.current = true;
  }, [loaded, activeMessages, setMessages]);

  useEffect(() => {
    if (!hydratedRef.current) return;
    if (messages.length === 0) return;
    persistMessages(messages);
  }, [messages, persistMessages]);
  const [activeTurnIdx, setActiveTurnIdx] = useState<number | null>(null);
  // Local add-to-trip state per assistant card — same lifecycle as the modal
  // concierge. Wired to the persistent itinerary in a follow-up.
  const [inTrip, setInTrip] = useState<Set<string>>(new Set());
  const scrollerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  // The preview centres itself in the panel, so the panel has to be the
  // positioned host it measures against.
  const panelRef = useRef<HTMLElement>(null);
  const { size: mapSize, cycle: cycleMapSize } = useChatMapSize();

  const toggleTrip = (id: string) =>
    setInTrip((s) => {
      const next = new Set(s);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  useThreadAnchor(scrollerRef, messages, loading);

  // Focus the input on mount
  useEffect(() => {
    const id = window.setTimeout(() => inputRef.current?.focus(), 80);
    return () => window.clearTimeout(id);
  }, []);

  // Track the latest assistant turn — that's what feeds the map. We
  // intentionally reset to "latest" whenever a new reply arrives so the
  // map always follows the freshest answer. The user can click an
  // earlier turn to re-focus the map on it.
  const assistantTurnIndexes = useMemo(
    () => messages.flatMap((m, i) => (m.kind === "assistant" && m.matches.length > 0 ? [i] : [])),
    [messages],
  );
  const latestAssistantIdx = assistantTurnIndexes.at(-1) ?? null;

  useEffect(() => {
    if (latestAssistantIdx == null) return;
    setActiveTurnIdx(latestAssistantIdx);
  }, [latestAssistantIdx]);

  // Push the active turn's match slugs into shared state so the map
  // filters down to them. Clears on unmount so leaving the chat tab
  // restores the full catalogue.
  useEffect(() => {
    if (activeTurnIdx == null) {
      setAiMatchSlugs([]);
      return;
    }
    const m = messages[activeTurnIdx];
    if (m && m.kind === "assistant") {
      setAiMatchSlugs(m.matches.map((x) => x.slug));
    }
  }, [activeTurnIdx, messages, setAiMatchSlugs]);

  useEffect(() => {
    return () => {
      setAiMatchSlugs([]);
    };
  }, [setAiMatchSlugs]);

  /**
   * `?resume=<sessionId>` reopens a saved conversation — how a forked share
   * link lands, and how a chat started on the phone is picked up here. Once,
   * and only into an empty thread: re-running it on every render would splice
   * the same turns in again.
   */
  const resumedRef = useRef(false);
  useEffect(() => {
    if (resumedRef.current) return;
    if (typeof window === "undefined") return;
    const sessionId = new URLSearchParams(window.location.search).get("resume");
    if (!sessionId) return;
    resumedRef.current = true;
    void resumeChat(sessionId).then((turns) => {
      if (!turns || turns.length === 0) return;
      setMessages(
        turns.map((t2, i) =>
          t2.role === "user"
            ? { kind: "user" as const, id: `resume-${i}`, text: t2.content }
            : { kind: "assistant" as const, id: `resume-${i}`, text: t2.content, matches: [] },
        ),
      );
      hydratedRef.current = true;
      // Drop the parameter so a refresh does not resume a second time. Only
      // that one: the rest of the query string is the guest's and stays.
      const url = new URL(window.location.href);
      url.searchParams.delete("resume");
      window.history.replaceState(null, "", `${url.pathname}${url.search}${url.hash}`);
    });
  }, [setMessages]);

  const submit = (query: string) => {
    const q = query.trim();
    if (!q || loading) return;
    setLastQuery(q);
    setInput("");
    const replaceFrom = editingId;
    setEditingId(null);
    void send(q, replaceFrom ? { replaceFrom } : undefined);
  };

  /** Hands a message back to the composer; the next send cuts the thread there. */
  const startEdit = (id: string, text: string) => {
    setEditingId(id);
    setInput(text);
    window.setTimeout(() => inputRef.current?.focus(), 0);
  };
  const cancelEdit = () => {
    setEditingId(null);
    setInput("");
  };
  /** Puts words in the composer without sending them ("Change" on a proposal). */
  const compose = (text: string) => {
    setEditingId(null);
    setInput(text);
    window.setTimeout(() => inputRef.current?.focus(), 0);
  };

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    submit(input);
  };

  /** Same conversation, composer back, refused question ready to send. */
  const resumeAfterSignIn = () => {
    const pending = gatedQuery;
    clearGate();
    if (pending) setInput(pending);
    window.setTimeout(() => inputRef.current?.focus(), 0);
  };

  return (
    <aside
      ref={panelRef}
      // `mj-dk-frame` paints the pewter band. The chat panel was the one
      // Discover panel without it — the browser column, the rail and every
      // card wear it, so the chat read as an unframed sheet beside them.
      className="mj-discover-browser mj-dk-frame mj-discover-chat"
      style={{
        // The column count follows the map, not pixel arithmetic: three
        // across until the map takes most of the row, then two so the cards
        // keep their photographs. `MatchGrid` reads this and falls back to
        // auto-fill where it is not set.
        ["--mj-match-grid" as string]:
          mapSize === "wide" ? "repeat(2, minmax(0, 1fr))" : "repeat(3, minmax(0, 1fr))",
        // Override the Browser's 24px padding — we manage spacing
        // ourselves so the header/composer can bleed to the edges.
        padding: 0,
        overflow: "hidden",
        minHeight: 0,
        // The panel's ground goes through the frame's fill layer, NOT through
        // `background`: the band is painted as background-image layers, and a
        // `background` shorthand here would reset them and take the edge with
        // it.
        ["--mj-dk-fill" as string]: `linear-gradient(180deg, ${SOFT.ground} 0%, ${SOFT.ground} 100%)`,
        // Sticky position and height come from the stylesheet, in the same
        // declaration as `.mj-discover-stage` — the panel and the map beside
        // it are one row and have to be one height. Setting them here is what
        // let the two drift apart. Internal scrolling stays in the thread.
      }}
      dir={lang === "ar" ? "rtl" : "ltr"}
    >
      <MatchPreviewProvider lang={lang} inTrip={inTrip} onAdd={toggleTrip} hostRef={panelRef}>
      <ChatHeader
        phone={phone}
        shareButton={
          history.activeSessionId ? (
            <ChatShareButton sessionId={history.activeSessionId} lang={lang} />
          ) : null
        }
        mapSizeButton={
          <ChatToolbarButton
            onClick={cycleMapSize}
            label={
              mapSize === "wide"
                ? t("discover.chat_map_shrink", { defaultValue: "Shrink map" })
                : t("discover.chat_map_expand", { defaultValue: "Expand map" })
            }
            icon={
              <Icon
                name={
                  mapSize === "wide" ? "ri-collapse-diagonal-line" : "ri-expand-diagonal-line"
                }
                size={12}
                aria-hidden="true"
              />
            }
          />
        }
        historyMenu={
          <ChatHistoryMenu
            conversations={history.conversations}
            activeId={history.activeId}
            onNewChat={() => {
              history.newChat();
              setEditingId(null);
              setMessages([]);
              hydratedRef.current = true;
              window.setTimeout(() => inputRef.current?.focus(), 0);
            }}
            onSelect={(id) => {
              const msgs = history.selectChat(id);
              setEditingId(null);
              setMessages(msgs);
            }}
            onDelete={(id) => history.deleteChat(id)}
          />
        }
      />

      <div
        ref={scrollerRef}
        style={{
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          padding: "8px 16px 12px",
          // The phone's empty chat sits its greeting at the bottom, next to
          // the box and the thumb, rather than at the top of a tall screen.
          ...(phone ? { display: "flex", flexDirection: "column" } : null),
        }}
      >
        {messages.length === 0 && !error ? (
          phone ? (
            <div style={{ marginTop: "auto", paddingTop: 16 }}>
              <ChatWelcome stack onPick={submit} />
            </div>
          ) : (
            <ChatWelcome compact onPick={submit} />
          )
        ) : null}

        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          {messages.map((m, i) =>
            m.kind === "user" ? (
              <UserBubble
                key={m.id}
                id={m.id}
                text={m.text}
                onEdit={loading ? undefined : () => startEdit(m.id, m.text)}
              />
            ) : m.kind === "pending" ? (
              <PendingTurn key={m.id} steps={m.steps} />
            ) : (
              <TurnRow
                key={m.id}
                index={i}
                clickable={m.matches.length > 0}
                isActive={i === activeTurnIdx}
                onActivate={() => setActiveTurnIdx(i)}
              >
                <AssistantTurn
                  message={m}
                  lang={lang}
                  inTrip={inTrip}
                  onAdd={toggleTrip}
                  onAddItinerary={addItineraryToTrip}
                  activeTripName={activeTrip?.name ?? null}
                  onRefine={submit}
                  onCompose={compose}
                  shareSessionId={history.activeSessionId}
                  conversationId={history.activeId}
                  isLatest={i === messages.length - 1 && isLive(m.id)}
                />
              </TurnRow>
            ),
          )}

          {error && (
            <ChatError
              offerTeam={retryFailed}
              onRetry={
                messages.some((m) => m.kind === "user")
                  ? retry
                  : lastQuery
                    ? () => submit(lastQuery)
                    : undefined
              }
            />
          )}
        </div>
      </div>

      {/* Same swap as the modal, edited in lockstep: the gate takes the
          composer's place, the thread above it stays exactly as it was, and
          there is no disabled field left behind. */}
      {gated ? (
        <div style={{ background: SOFT.ground, padding: "8px 12px 12px" }}>
          <ConciergeSignInGate compact onSignedIn={resumeAfterSignIn} />
        </div>
      ) : (
        <ChatComposer
          value={input}
          onChange={setInput}
          onSubmit={onSubmit}
          loading={loading}
          inputRef={inputRef}
          compact
          onStop={stop}
          editing={!!editingId}
          onCancelEdit={cancelEdit}
        />
      )}
      </MatchPreviewProvider>
    </aside>
  );
}

/**
 * The Discover panel's one extra: clicking an answer re-pins the map on it.
 * The active turn wears a gold hairline, the phone's focus colour, instead
 * of the old purple.
 */
function TurnRow({
  index,
  clickable,
  isActive,
  onActivate,
  children,
}: {
  index: number;
  clickable: boolean;
  isActive: boolean;
  onActivate: () => void;
  children: ReactNode;
}) {
  const { t } = useTranslation();
  return (
    <div
      role={clickable ? "button" : undefined}
      tabIndex={clickable ? 0 : undefined}
      onClick={clickable ? onActivate : undefined}
      onKeyDown={
        clickable
          ? (e) => {
              if (e.target !== e.currentTarget) return;
              if (e.key === "Enter" || e.key === " ") onActivate();
            }
          : undefined
      }
      style={{
        padding: 8,
        margin: -8,
        borderRadius: 18,
        cursor: clickable ? "pointer" : "default",
        border: isActive ? `1px solid ${SOFT.gold}` : "1px solid transparent",
        transition: "border-color 0.15s ease",
      }}
      aria-label={
        clickable
          ? t("discover.chat_focus_turn", { defaultValue: "Focus map on answer {{n}}", n: index + 1 })
          : undefined
      }
    >
      {children}
    </div>
  );
}

function ChatHeader({
  historyMenu,
  mapSizeButton,
  shareButton,
  phone,
}: {
  historyMenu?: ReactNode;
  mapSizeButton?: ReactNode;
  shareButton?: ReactNode;
  phone?: DiscoverPhoneChat;
}) {
  const { t } = useTranslation();
  if (phone) {
    // Full-screen on a phone (option C, 2026-10-12): a way back, the title on
    // one line, and only History and New chat, as icons. There is no map on
    // the phone chat, so no map-size control; the row ran off the screen.
    return (
      <header className="mj-chat-head--phone" style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 14px 6px" }}>
        <button
          type="button"
          onClick={phone.onBack}
          aria-label={t("common.back", { defaultValue: "Back" })}
          className="mj-press grid h-10 w-10 shrink-0 place-items-center rounded-full"
          style={{ background: SOFT.controlFill, boxShadow: `0 0 0 1.5px ${SOFT.controlEdge}`, color: SOFT.ink }}
        >
          <Icon name="ri-arrow-left-line" size={18} aria-hidden="true" className="rtl:-scale-x-100" />
        </button>
        <div className="min-w-0 flex-1 break-words [text-wrap:balance]" style={{ fontSize: 18, fontWeight: 700, color: SOFT.ink }}>
          {t("ai_concierge.title", { defaultValue: "MaJourneys AI" })}
        </div>
        <ChatToolbar>{historyMenu}</ChatToolbar>
      </header>
    );
  }
  return (
    <header
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 12,
        padding: "16px 16px 8px",
      }}
    >
      <div style={{ fontSize: 15, fontWeight: 700, color: SOFT.ink }}>
        {t("ai_concierge.title", { defaultValue: "MaJourneys AI" })}
      </div>
      <ChatToolbar>
        {mapSizeButton}
        {shareButton}
        {historyMenu}
      </ChatToolbar>
    </header>
  );
}
